import { GridSmartViewsView } from '../../../../grid/gridSmartViews/view/GridSmartViewsView';
import { SectionType, SmartViewItemConfiguration, SmartViewSection } from '../../../../grid/gridSmartViews/types';
import { htmlDecode } from '../../../../utils/escape.utils';
import {
  DefaultSmartViewId,
  IntegrityMonitorInlineActions,
  MappingViewIdToType,
  SmartViewInfo,
  ViewType,
} from '../types';
import {
  Column,
  DISPLAYED_COLUMNS_RULES,
  IM_RULES_DISPLAYED_FILTERS,
  Filter,
  DISPLAYED_COLUMNS_RULES_NO_ACTIONS,
} from '../../integrityMonitorRulesGrid/types';
import { IntegrityMonitorIdentifier } from '../../../common/utils/types';
import { Rights } from '@nirvana/shared/constants';

export class IntegrityMonitorRulesSmartViewsView extends GridSmartViewsView {
  public static getViewTypeFromId(id: string): ViewType {
    const viewType = MappingViewIdToType[id];

    if (!viewType) {
      // Rule Set views contain unknown ids from db
      return ViewType.RULE_SET;
    }

    return viewType;
  }

  public setTranslatedStrings() {
    super.setTranslatedStrings();

    const integrityMonitorRulesTranslations = this.stringsTranslator.getIntegrityMonitorStrings().integrityMonitorRules;
    this.translations.integrityMonitorRules = integrityMonitorRulesTranslations;
    this.translations.emptySavedViewsText = integrityMonitorRulesTranslations.smartViews.emptyRuleSetSmartView;
    this.translations.deleteViewFormDialog.title =
      integrityMonitorRulesTranslations.smartViews.deleteViewFormDialogTitle;
    this.translations.integrityMonitorRules.smartViews.inlineActions = {
      ...this.stringsTranslator.getInlineActionsCommonStrings(),
      ...this.translations.integrityMonitorRules.smartViews.inlineActions,
    };
  }

  private createSmartItemViewConfigs(
    viewsInfo: Array<SmartViewInfo>,
    activeViewId: string
  ): Array<SmartViewItemConfiguration<Column, Filter, ViewType>> {
    return viewsInfo.map(viewInfo => {
      return {
        id: viewInfo._id,
        active: viewInfo._id === activeViewId,
        viewName: htmlDecode(viewInfo.name),
        viewType: IntegrityMonitorRulesSmartViewsView.getViewTypeFromId(viewInfo._id),
        filters: viewInfo.filters,
        displayedColumns: viewInfo.displayedColumns,
        isCompactView: false,
        pageSize: 50,
        pageNumber: 1,
      };
    });
  }

  public getRulesSection(activeViewId: string): SmartViewSection<Column, Filter, ViewType> {
    return {
      type: SectionType.DEFAULT,
      sectionTitle: this.dependencies.translateFn('Rules'),
      inlineActions: [],
      items: this.createSmartItemViewConfigs(this.getRuleViewsInfo(), activeViewId),
    };
  }

  public getRuleSetsSection(
    ruleSetIdentifiers: Array<IntegrityMonitorIdentifier>,
    activeViewId: string
  ): SmartViewSection<Column, Filter, ViewType> {
    const sectionTitleWithCounter = this.dependencies.translateFn('Rule sets ({ruleSetsCount})', {
      ruleSetsCount: ruleSetIdentifiers.length,
    });

    const ruleSetSmartViewItemConfigs = ruleSetIdentifiers.map(ruleSet => {
      return {
        ...ruleSet,
        displayedColumns: DISPLAYED_COLUMNS_RULES_NO_ACTIONS,
        filters: IM_RULES_DISPLAYED_FILTERS,
      } as SmartViewInfo;
    });

    return {
      type: SectionType.SAVED_VIEWS,
      sectionTitle: sectionTitleWithCounter,
      inlineActions: this.getRuleSetViewsInlineActions(),
      items: this.createSmartItemViewConfigs(ruleSetSmartViewItemConfigs, activeViewId),
    };
  }

  private getRuleSetViewsInlineActions() {
    if (!Application.Main.hasAllRights([Rights.MANAGE_POLICIES_WRITE])) {
      return [];
    }

    if (!this.dependencies.canModifyRulesAndRuleSets()) {
      return [];
    }

    return [
      // This option will be uncommented when "Add to policy" functionality will be implemented
      // {
      //   title: this.translations.integrityMonitorRules.smartViews.inlineActions.addToPolicy,
      //   value: IntegrityMonitorInlineActions.ADD_TO_POLICY,
      // },
      {
        title: this.translations.integrityMonitorRules.smartViews.inlineActions.edit,
        value: IntegrityMonitorInlineActions.EDIT,
      },
      {
        title: this.translations.integrityMonitorRules.smartViews.inlineActions.delete,
        value: IntegrityMonitorInlineActions.DELETE,
        customClass: 'danger',
      },
    ];
  }

  private getRuleViewsInfo(): Array<SmartViewInfo> {
    return [
      {
        _id: DefaultSmartViewId.ALL_RULES,
        name: this.translations.integrityMonitorRules.smartViews.allRules,
        displayedColumns: DISPLAYED_COLUMNS_RULES,
        filters: IM_RULES_DISPLAYED_FILTERS,
      },
      {
        _id: DefaultSmartViewId.DEFAULT_OS_RULES,
        name: this.translations.integrityMonitorRules.smartViews.defaultOsRules,
        displayedColumns: DISPLAYED_COLUMNS_RULES,
        filters: IM_RULES_DISPLAYED_FILTERS,
      },
      {
        _id: DefaultSmartViewId.DEFAULT_APPLICATION_RULES,
        name: this.translations.integrityMonitorRules.smartViews.defaultApplicationRules,
        displayedColumns: DISPLAYED_COLUMNS_RULES,
        filters: IM_RULES_DISPLAYED_FILTERS,
      },
      {
        _id: DefaultSmartViewId.CUSTOM_RULES,
        name: this.translations.integrityMonitorRules.smartViews.customRules,
        displayedColumns: DISPLAYED_COLUMNS_RULES,
        filters: IM_RULES_DISPLAYED_FILTERS,
      },
    ];
  }
}
